import { getApiUrl } from '@/lib/apiConfig';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

export interface VehicleInspectionChecklistItemInput {
  item?: string;
  status?: string;
  notes?: string;
  /** Per-item failure photo (data URL or cloud URL) — forwarded to record_inspection */
  photoUrl?: string;
}

export interface VehicleInspectionRawInput {
  vehicle_id?: string;
  vehicle_registration?: string;
  inspection_type?: string;
  odometer_reading?: number;
  /** Structured odometer payload from SmartTask (e.g. OdometerCapturePayload) or loose JSON. */
  odometer_capture?: unknown;
  fuel_level?: string | null;
  checklist_items?: VehicleInspectionChecklistItemInput[];
  photos?: Partial<Record<string, string>>;
  overall_status?: string;
  notes?: string;
  issues_found?: string[];
  ai_analysis?: Record<string, unknown> | null;
  plate_verification?: Record<string, unknown> | null;
  /** Admin-presence verification from VehicleModal when require_admin_present is on */
  admin_verification?: Record<string, unknown> | null;
  /** Rule 3: Handover partner user ID — clears partner's inspection_pending */
  handover_partner_user_id?: number;
  /** Rule 5: Random inspection task ID — marks the fleet task completed */
  random_inspection_task_id?: string;
  /** Scheduled inspection task ID — marks the fleet task completed */
  scheduled_inspection_task_id?: string;
}

export interface VehicleInspectionLocationInput {
  latitude?: number | null;
  longitude?: number | null;
}

export interface OfflineVehicleInspectionSource {
  data: VehicleInspectionRawInput;
  location?: VehicleInspectionLocationInput | null;
}

export interface VehicleInspectionActionPayload extends Record<string, unknown> {
  action: 'record_inspection';
  vehicle_id?: string;
  vehicle_registration?: string;
  metadata: Record<string, unknown>;
  offline_inspection_source?: OfflineVehicleInspectionSource | null;
}

export function normalizeInspectionSourceFromPayload(payload: VehicleInspectionActionPayload): OfflineVehicleInspectionSource {
  const metadata = payload?.metadata && typeof payload.metadata === 'object' ? payload.metadata : {} as Record<string, unknown>;
  const metadataPhotos = Array.isArray(metadata.photos)
    ? Object.fromEntries(
        (metadata.photos as Record<string, unknown>[])
          .filter((photo) => photo?.area && typeof photo?.dataUrl === 'string')
          .map((photo) => [String(photo.area), photo.dataUrl as string]),
      )
    : {};

  return {
    data: {
      vehicle_id: payload?.vehicle_id as string | undefined,
      vehicle_registration: payload?.vehicle_registration as string | undefined,
      inspection_type: metadata.inspection_type as string | undefined,
      odometer_reading: metadata.odometer_reading as number | undefined,
      odometer_capture: (metadata.odometer_capture as Record<string, unknown> | undefined) || null,
      fuel_level: (metadata.fuel_level as string | undefined) || null,
      checklist_items: Array.isArray(metadata.items)
        ? (metadata.items as Record<string, unknown>[]).map((item) => ({
            item: (item?.label as string) || (item?.key as string) || '',
            status: (item?.status as string) || (item?.passed ? 'ok' : 'fail'),
            notes: (item?.notes as string) || '',
            photoUrl: (item?.photoUrl as string) || undefined,
          }))
        : [],
      photos: metadataPhotos,
      overall_status: metadata.result === 'fail' ? 'fail' : 'pass',
      notes: metadata.notes as string | undefined,
      issues_found: Array.isArray(metadata.issues_found) ? (metadata.issues_found as string[]) : [],
      ai_analysis: (metadata.ai_analysis as Record<string, unknown> | undefined) || null,
      plate_verification: (metadata.plate_verification as Record<string, unknown> | undefined) || null,
      admin_verification: (metadata.admin_verification as Record<string, unknown> | undefined) || null,
      handover_partner_user_id: metadata.handover_partner_user_id as number | undefined,
      random_inspection_task_id: metadata.random_inspection_task_id as string | undefined,
      scheduled_inspection_task_id: metadata.scheduled_inspection_task_id as string | undefined,
    },
    location: (metadata.location as VehicleInspectionLocationInput | undefined) || null,
  };
}

export function buildVehicleInspectionActionPayload(params: {
  inspection: VehicleInspectionRawInput;
  location?: VehicleInspectionLocationInput | null;
  includeOfflineSource?: boolean;
}): VehicleInspectionActionPayload {
  const { inspection, location, includeOfflineSource = false } = params;
  const hasLocation = Number.isFinite(location?.latitude) && Number.isFinite(location?.longitude);

  const payload: VehicleInspectionActionPayload = {
    action: 'record_inspection',
    vehicle_id: inspection.vehicle_id,
    vehicle_registration: inspection.vehicle_registration,
    metadata: {
      inspection_type: inspection.inspection_type || 'pre_trip',
      result: inspection.overall_status === 'fail' ? 'fail' : 'pass',
      items: (inspection.checklist_items || []).map((ci) => ({
        key: ci.item,
        label: ci.item,
        passed: ci.status === 'ok',
        status: ci.status,
        notes: ci.notes || '',
        photoUrl: ci.photoUrl || undefined,
      })),
      notes: inspection.notes,
      photos: inspection.photos
        ? Object.entries(inspection.photos).map(([slot, dataUrl]) => ({
            key: slot,
            area: slot,
            dataUrl,
          }))
        : [],
      fuel_level: inspection.fuel_level,
      odometer_reading: inspection.odometer_reading,
      odometer_capture: inspection.odometer_capture || undefined,
      plate_verification: inspection.plate_verification || undefined,
      issues_found: inspection.issues_found,
      ai_analysis: inspection.ai_analysis || undefined,
      // Admin-presence verification — previously dropped between VehicleModal
      // and record_inspection, leaving custom_fields.admin_confirmed dead.
      ...(inspection.admin_verification ? {
        admin_verification: inspection.admin_verification,
      } : {}),
      location: hasLocation
        ? { latitude: location?.latitude, longitude: location?.longitude }
        : undefined,
      // Rule 3: Handover partner — clears partner's inspection_pending state
      ...(inspection.handover_partner_user_id ? {
        handover_partner_user_id: inspection.handover_partner_user_id,
      } : {}),
      // Rule 5: Random inspection task — marks the fleet task completed
      ...(inspection.random_inspection_task_id ? {
        random_inspection_task_id: inspection.random_inspection_task_id,
      } : {}),
      // Scheduled inspection task — marks the fleet task completed
      ...(inspection.scheduled_inspection_task_id ? {
        scheduled_inspection_task_id: inspection.scheduled_inspection_task_id,
      } : {}),
    },
  };

  if (includeOfflineSource) {
    payload.offline_inspection_source = {
      data: {
        ...inspection,
        photos: inspection.photos ? { ...inspection.photos } : {},
        checklist_items: Array.isArray(inspection.checklist_items)
          ? inspection.checklist_items.map((item) => ({ ...item }))
          : [],
      },
      location: location || null,
    };
  }

  return payload;
}

export async function enrichVehicleInspectionActionPayload(params: {
  payload: VehicleInspectionActionPayload;
  token: string;
}): Promise<VehicleInspectionActionPayload> {
  const { payload, token } = params;
  if (!token) {
    return payload;
  }

  const source = payload.offline_inspection_source || normalizeInspectionSourceFromPayload(payload);
  const sourceData = source.data || {};
  const sourceLocation = source.location || null;
  const nextPayload: VehicleInspectionActionPayload = {
    ...payload,
    metadata: {
      ...(payload.metadata || {}),
    },
  };

  // Fresh online submissions skip this blocking call — the server runs the
  // same analysis in the background (see record_inspection deferred analysis)
  // so the driver can submit as soon as the required photos are captured.
  // Offline replays still enrich here so queued inspections carry results even
  // if the deferred server pass fails.
  const isOfflineReplay = !!payload.offline_inspection_source;
  if (isOfflineReplay && !nextPayload.metadata.ai_analysis && sourceData.photos && Object.keys(sourceData.photos).length > 0) {
    try {
      const analysisRes = await fetch(getApiUrl('/api/v1/work-items/analyze-inspection'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vehicle_id: sourceData.vehicle_id,
          photos: sourceData.photos,
          inspection_type: sourceData.inspection_type,
          vehicle_name: sourceData.vehicle_registration,
        }),
      });

      const analysisData = await analysisRes.json().catch(() => null);
      if (analysisRes.ok && analysisData?.success) {
        nextPayload.metadata.ai_analysis = {
          results: Object.fromEntries(
            ((analysisData.slots as Record<string, unknown>[]) || []).map((slot) => [slot.slot as string, {
              condition: slot.condition,
              damage_detected: slot.damage_detected,
              damage_areas: (slot.damage_areas as string[]) || [],
              new_damage: (slot.new_damage as string[]) || [],
              visible_issues: (slot.visible_issues as string[]) || [],
              recommendations: (slot.recommendations as string[]) || [],
              comparison_summary: slot.comparison_summary || null,
              confidence: (slot.confidence as number) || 0,
            }]),
          ),
          has_previous_inspection: analysisData.has_previous_inspection || false,
          previous_inspection_date: analysisData.previous_inspection_date || null,
          overall_condition: analysisData.summary?.overall_condition || 'unknown',
          total_damage_areas: analysisData.summary?.total_damage_areas || 0,
          new_damage_count: analysisData.summary?.new_damage_count || 0,
        };
        nextPayload.metadata.odometer_reading = (nextPayload.metadata.odometer_reading as number | undefined)
          || (analysisData.dashboard_odometer?.reading as number | undefined)
          || (nextPayload.metadata.odometer_reading as number | undefined);
      }
    } catch (error) {
      if (!isSilentFetchError(error)) {
        console.warn('[SmartTaskVehicleInspectionPayload] Deferred inspection AI replay failed, continuing without it:', error);
      }
    }
  }

  // Plate verification enrichment must also be offline-replay only: on fresh
  // submissions the modal already ran the ANPR/license-disk gate when the
  // client config requires it, so calling verify-plate here would add up to
  // two blocking Gemini calls — and would run even when ANPR isn't required.
  if (isOfflineReplay && !nextPayload.metadata.plate_verification && sourceData.vehicle_registration) {
    try {
      const slots = (['front', 'rear'] as const).filter((slot) => Boolean(sourceData.photos?.[slot]));
      if (slots.length > 0) {
        const results = await Promise.all(slots.map(async (slot) => {
          const res = await fetch(getApiUrl('/api/v1/work-items/verify-plate'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              photo: sourceData.photos?.[slot],
              expected_registration: sourceData.vehicle_registration,
              slot,
            }),
          });
          const body = await res.json().catch(() => null);
          return { slot, body };
        }));

        const front = results.find((result) => result.slot === 'front');
        const rear = results.find((result) => result.slot === 'rear');
        const matched = results.some((result) => result.body?.matched);

        nextPayload.metadata.plate_verification = {
          expected_registration: sourceData.vehicle_registration,
          verified: matched,
          verification_method: 'gemini_lnpr',
          front: front?.body ? {
            status: front.body.plate_detected ? (front.body.matched ? 'matched' : 'mismatch') : 'no_plate',
            plate_read: front.body.plate_text || null,
            plate_normalized: front.body.plate_normalized || null,
            matched: front.body.matched || false,
            confidence: front.body.confidence || 0,
          } : null,
          rear: rear?.body ? {
            status: rear.body.plate_detected ? (rear.body.matched ? 'matched' : 'mismatch') : 'no_plate',
            plate_read: rear.body.plate_text || null,
            plate_normalized: rear.body.plate_normalized || null,
            matched: rear.body.matched || false,
            confidence: rear.body.confidence || 0,
          } : null,
        };
      }
    } catch (error) {
      if (!isSilentFetchError(error)) {
        console.warn('[SmartTaskVehicleInspectionPayload] Deferred plate verification failed, continuing without it:', error);
      }
    }
  }

  const odometerCapture = (nextPayload.metadata.odometer_capture || sourceData.odometer_capture) as Record<string, unknown> | null;
  if (isOfflineReplay && odometerCapture?.photo_data && (!odometerCapture.ai_validation || odometerCapture.ai_validation === 'pending_offline')) {
    try {
      const odometerRes = await fetch(getApiUrl('/api/v1/work-items/fleet/verify-odometer'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: odometerCapture.photo_data,
          expected_reading: odometerCapture.reading,
          vehicle_name: sourceData.vehicle_registration,
        }),
      });

      const odometerData = await odometerRes.json().catch(() => null);
      if (odometerRes.ok && odometerData?.success && odometerData.odometer) {
        nextPayload.metadata.odometer_capture = {
          ...(odometerCapture as Record<string, unknown>),
          ai_confidence: odometerData.odometer.confidence ?? null,
          ai_validation: odometerData.odometer.validation ?? null,
          detected_reading: odometerData.odometer.detected_reading ?? null,
          pending_verification: false,
        };
        nextPayload.metadata.odometer_reading = (nextPayload.metadata.odometer_reading as number | undefined)
          || (odometerData.odometer.detected_reading as number | undefined)
          || (nextPayload.metadata.odometer_reading as number | undefined);
      }
    } catch (error) {
      if (!isSilentFetchError(error)) {
        console.warn('[SmartTaskVehicleInspectionPayload] Deferred odometer verification failed, continuing without it:', error);
      }
    }
  }

  if (!nextPayload.metadata.location && Number.isFinite(sourceLocation?.latitude) && Number.isFinite(sourceLocation?.longitude)) {
    nextPayload.metadata.location = {
      latitude: sourceLocation?.latitude,
      longitude: sourceLocation?.longitude,
    };
  }

  return nextPayload;
}

export function stripVehicleInspectionOfflineSource(payload: VehicleInspectionActionPayload): Record<string, unknown> {
  const { offline_inspection_source, ...requestPayload } = payload;
  return requestPayload;
}
